import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'

export default function AdminUserManagement() {
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'buyer',
    full_name: '',
  })
  const [resetUserId, setResetUserId] = useState(null)

  const { data: usersData, isLoading, refetch } = useQuery({
    queryKey: ['admin-all-users'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_get_all_profiles')
      if (error) throw error
      return data
    },
  })

  const disableUserMutation = useMutation({
    mutationFn: async (userId) => {
      const { error } = await supabase.rpc('admin_disable_user', { target_user_id: userId })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-all-users'])
      alert('User disabled successfully')
    },
    onError: (error) => {
      alert(`Failed to disable user: ${error.message}`)
    },
  })

  const enableUserMutation = useMutation({
    mutationFn: async (userId) => {
      const { error } = await supabase.rpc('admin_enable_user', { target_user_id: userId })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-all-users'])
      alert('User enabled successfully')
    },
    onError: (error) => {
      alert(`Failed to enable user: ${error.message}`)
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ userId, userEmail }) => {
      const { error: rpcError } = await supabase.rpc('admin_reset_user_password', { target_user_id: userId })
      if (rpcError) throw rpcError

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(userEmail, {
        redirectTo: `${window.location.origin}/signin`,
      })
      if (resetError) throw resetError
    },
    onSuccess: () => {
      setResetUserId(null)
      alert('Password reset email sent successfully')
    },
    onError: (error) => {
      setResetUserId(null)
      alert(`Failed to reset password: ${error.message}`)
    },
  })

  const inviteUserMutation = useMutation({
    mutationFn: async (userData) => {
      const { error: rpcError } = await supabase.rpc('admin_invite_user', {
        user_email: userData.email,
        user_role: userData.role,
        user_full_name: userData.full_name,
      })
      if (rpcError) throw rpcError

      const randomPassword = Math.random().toString(36).slice(-12) + Math.random().toString(36).slice(-12).toUpperCase() + '1!'
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: userData.email,
        password: randomPassword,
        options: {
          data: {
            full_name: userData.full_name || '',
            role: userData.role,
          },
        },
      })
      if (signUpError) throw signUpError

      return data
    },
    onSuccess: () => {
      setShowInviteModal(false)
      setInviteData({ email: '', role: 'buyer', full_name: '' })
      queryClient.invalidateQueries(['admin-all-users'])
      alert('User invited successfully. They will receive a confirmation email.')
    },
    onError: (error) => {
      alert(`Failed to invite user: ${error.message}`)
    },
  })

  const users = usersData || []
  const filteredUsers = users.filter(
    (user) =>
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleDisableUser = (userId) => {
    if (confirm('Are you sure you want to disable this user? They will not be able to log in.')) {
      disableUserMutation.mutate(userId)
    }
  }

  const handleEnableUser = (userId) => {
    if (confirm('Are you sure you want to enable this user?')) {
      enableUserMutation.mutate(userId)
    }
  }

  const handleResetPassword = (userId, userEmail) => {
    if (confirm('Are you sure you want to send a password reset email to this user?')) {
      resetPasswordMutation.mutate({ userId, userEmail })
      setResetUserId(userId)
    }
  }

  const handleInviteUser = () => {
    if (!inviteData.email || !inviteData.role) {
      alert('Please fill in all required fields')
      return
    }
    inviteUserMutation.mutate(inviteData)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner spinner-lg text-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search users by name or email..."
              className="input pl-10"
            />
          </div>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Invite User
        </button>
      </div>

      <div className="text-sm text-secondary-600">
        Showing {filteredUsers.length} of {users.length} users
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary-50 border-b border-secondary-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-secondary-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-secondary-900">{user.full_name || 'No name'}</div>
                      <div className="text-sm text-secondary-600">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`badge ${
                        user.role === 'admin'
                          ? 'badge-error'
                          : user.role === 'developer'
                          ? 'badge-primary'
                          : 'badge-secondary'
                      }`}
                    >
                      {user.role}
                    </span>
                    {user.admin_level && (
                      <span
                        className={`ml-2 badge ${
                          user.admin_level === 'super_admin' ? 'badge-error' : 'badge-warning'
                        }`}
                      >
                        {user.admin_level === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`badge ${
                        user.is_disabled ? 'badge-error' : 'badge-success'
                      }`}
                    >
                      {user.is_disabled ? 'Disabled' : 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                    {new Date(user.created_at).toLocaleDateString('en-NZ')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                    <button
                      onClick={() => handleResetPassword(user.id, user.email)}
                      disabled={resetUserId === user.id || resetPasswordMutation.isPending}
                      className="text-primary-600 hover:text-primary-900 font-medium disabled:opacity-50"
                      title="Reset password"
                    >
                      {resetUserId === user.id ? 'Sending...' : 'Reset'}
                    </button>
                    {user.is_disabled ? (
                      <button
                        onClick={() => handleEnableUser(user.id)}
                        disabled={enableUserMutation.isPending}
                        className="text-green-600 hover:text-green-900 font-medium disabled:opacity-50"
                      >
                        Enable
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDisableUser(user.id)}
                        disabled={disableUserMutation.isPending}
                        className="text-red-600 hover:text-red-900 font-medium disabled:opacity-50"
                      >
                        Disable
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-secondary-500">No users found matching your search</p>
          </div>
        )}
      </div>

      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="card max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-secondary-900 mb-4">Invite User</h3>

            <div className="space-y-4 mb-6">
              <div>
                <label className="label">Email Address*</label>
                <input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData((prev) => ({ ...prev, email: e.target.value }))}
                  className="input"
                  placeholder="user@example.com"
                  required
                />
              </div>

              <div>
                <label className="label">Full Name</label>
                <input
                  type="text"
                  value={inviteData.full_name}
                  onChange={(e) =>
                    setInviteData((prev) => ({ ...prev, full_name: e.target.value }))
                  }
                  className="input"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="label">Role*</label>
                <select
                  value={inviteData.role}
                  onChange={(e) => setInviteData((prev) => ({ ...prev, role: e.target.value }))}
                  className="input"
                  required
                >
                  <option value="buyer">Buyer</option>
                  <option value="developer">Developer</option>
                </select>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                An invitation email will be sent to the user with instructions to set up their
                account.
              </p>
            </div>

            {inviteUserMutation.error && (
              <div className="alert-error mb-4">{inviteUserMutation.error.message}</div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowInviteModal(false)
                  setInviteData({ email: '', role: 'buyer', full_name: '' })
                }}
                className="btn-outline flex-1"
                disabled={inviteUserMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleInviteUser}
                className="btn-primary flex-1"
                disabled={inviteUserMutation.isPending}
              >
                {inviteUserMutation.isPending ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
